import { createGlobalState } from "react-hooks-global-state";
import hash from "object-hash";
import { Chat } from "@converge-collective/common/models/Chat";
import { WithID } from "@converge-collective/common/models/Base";
import { LiteProfile } from "@converge-collective/common/models/Profile";
import { useEffect } from "react";
import { isEmpty } from "lodash";
import { useFirestore } from "reactfire";
import { useHash } from "react-use";

/**
 * State flows from the hash to the global state hook
 */
const initState: {
  // determines whether the chat box is open or not - this gets set when the url
  // hash changes (see useChatStateWithHash)
  chatOpen: boolean;
  // store enough data on chatToOpen that it can be created if it doesn't
  // already exist
  chatToOpen?: Pick<WithID<Chat>, "id" | "from" | "members">;
  // the selected chat (assumes it already exists in firestore)
  selectedChatId?: string;
} = {
  chatOpen: false,
  chatToOpen: undefined,
  selectedChatId: undefined,
};

export const { useGlobalState, getGlobalState, setGlobalState } =
  createGlobalState(initState);

/**
 * Main hook that initiates and handles the chat state
 * @returns void
 */
export const useChatStateWithHash = (): void => {
  // 1. wire up the global state with the hash from url
  const [hash, setHash] = useHash();
  console.log("useChatStateWithHash", { hash });

  useEffect(() => {
    console.log("chat useEffect hash", { hash });
    // remove the # at the beginning of the string
    const hashFragments = (hash ?? "").replace(/^#/, "").split("/");
    if (hashFragments[0] === "chat") {
      const chatId = hashFragments[1];
      setGlobalState("chatOpen", true);
      console.log("setGlobalState hash set chatOpen true");
      if (!isEmpty(chatId)) {
        setGlobalState("selectedChatId", chatId);
        console.log("hash set selectedChatId");
      }
    } else {
      // close chat panel
      closeChat();
    }
  }, [hash]);

  // 2. and also ensure chats exist when being opened
  const firestore = useFirestore();
  const chatToOpen = getGlobalState("chatToOpen");
  useEffect(() => {
    const ensureChat = async () => {
      console.log("ensureChat", { chatToOpen });
      if (chatToOpen) {
        const chatRef = firestore.doc(`chats/${chatToOpen.id}`);
        console.log("ensure chat", { chatToOpen, path: chatRef.path });
        const chat = await chatRef.get();
        if (!chat.exists) {
          const { from, members } = chatToOpen;
          const newChat: Chat = {
            from,
            members,
            lastActivityAt: new Date(),
            memberUids: members.map((m) => m.id),
            recentMessages: [],
          };
          console.log("creating chat", { newChat });
          await chatRef.set(newChat);
        }
        // after ensuring the chat exists set selectedChatId so the UI can
        // respond and reset chatToOpen
        // setGlobalState("selectedChatId", chatToOpen.id);
        const newHash = chatHashUrl(chatToOpen.id);
        console.log("ensureChat newHash", { newHash });
        setHash(newHash);
        setGlobalState("chatToOpen", undefined);
      }
    };

    // if a selected chat gets set, ensure the group chat exists
    ensureChat();
  }, [setHash, firestore, chatToOpen]);
};

export const chatHashUrl = (chatId: string): string => `#chat/${chatId}`;

const closeChat = (): void => {
  console.log("setGlobalState closeChat - setting chatOpen = false");
  setGlobalState("chatOpen", false);
};

// first ensures the chat exists and then sets global state to open it
export const openGroupChat = (
  from: LiteProfile,
  members: LiteProfile[]
): void => {
  const id = groupId(members.map((m) => m.id));
  setGlobalState("chatOpen", true);
  setGlobalState("chatToOpen", { id, from, members });
  console.log("openGroupChat", { id, from, members });
};

/** Returns a unique id for a group chat based on a hash of the sorted member uuids */
export const groupId = (uuids: string[]): string => {
  const sorted = uuids.sort();
  return hash(sorted.join(""));
};
